/**
 * Frente E/Q21 — digitação guiada da jornada (horários de Entrada e Saída).
 *
 * Três regras, iguais no espelho, na grade de transcrição e na auditoria do Registro de
 * Atividade:
 *  1. campo obrigatório de jornada vazio fica destacado (`JOURNEY_EMPTY_CLASSES`);
 *  2. ao abrir a tela/linha, o foco vai para o primeiro campo vazio;
 *  3. Tab num campo de jornada pula para o PRÓXIMO campo de jornada vazio. Sem vazio
 *     depois, ou com modificador (Shift+Tab volta), o Tab nativo segue intacto.
 *
 * Mora em `src/utils` porque o consumidor da grade é o design system
 * (`AttendanceSheetCard`), que não pode importar de `src/modules`.
 */

/** Marca um input como campo de jornada obrigatório — só quem o componente decidiu exigir. */
export const JOURNEY_FIELD_ATTR = 'data-journey-field'

/** Destaque do obrigatório vazio: tracejado vermelho, distinto do laranja de "editado". */
export const JOURNEY_EMPTY_CLASSES = 'border-negative border-dashed bg-negative/5'

/** Atributos de um input de jornada: marca + `aria-required` só quando exigido (use com `v-bind`). */
export function journeyFieldAttrs(required: boolean): Record<string, string> {
  return required ? { [JOURNEY_FIELD_ATTR]: '', 'aria-required': 'true' } : {}
}

const JOURNEY_SELECTOR = `input[${JOURNEY_FIELD_ATTR}]`
const EDITABLE_INPUT_SELECTOR =
  'input:not([type="checkbox"]):not([type="radio"]):not([type="hidden"]):not([type="button"])'

export function isJourneyValueEmpty(value: unknown): boolean {
  return String(value ?? '').trim() === ''
}

export function isPlainTab(
  event: Pick<KeyboardEvent, 'key' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey'>
): boolean {
  return event.key === 'Tab' && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey
}

function isAvailable(input: HTMLInputElement): boolean {
  return !input.disabled && !input.readOnly
}

function emptyInputs(root: ParentNode, selector: string): HTMLInputElement[] {
  return Array.from(root.querySelectorAll<HTMLInputElement>(selector)).filter(
    (input) => isAvailable(input) && isJourneyValueEmpty(input.value)
  )
}

function focusFirst(
  root: ParentNode | null | undefined,
  selector: string
): HTMLInputElement | null {
  if (!root) return null
  const target = emptyInputs(root, selector)[0] ?? null
  target?.focus()
  return target
}

/** Regra 2 — primeiro campo de jornada vazio e editável, em ordem de DOM. */
export function focusFirstEmptyJourneyField(
  root: ParentNode | null | undefined
): HTMLInputElement | null {
  return focusFirst(root, JOURNEY_SELECTOR)
}

/**
 * Regra 2 para uma linha recém-aberta que ainda não tem nem identidade (linha extra do
 * espelho): o primeiro campo vazio é o nome do médico, não o horário.
 */
export function focusFirstEmptyInput(root: ParentNode | null | undefined): HTMLInputElement | null {
  return focusFirst(root, EDITABLE_INPUT_SELECTOR)
}

/** Regra 3 — devolve `true` quando interceptou o Tab (e já moveu o foco). */
export function handleJourneyTab(
  event: KeyboardEvent,
  root: ParentNode | null | undefined
): boolean {
  if (!root || !isPlainTab(event)) return false
  const current = event.target
  if (!(current instanceof HTMLInputElement) || !current.hasAttribute(JOURNEY_FIELD_ATTR)) {
    return false
  }

  const next = emptyInputs(root, JOURNEY_SELECTOR).find(
    (input) =>
      input !== current &&
      Boolean(current.compareDocumentPosition(input) & Node.DOCUMENT_POSITION_FOLLOWING)
  )
  if (!next) return false

  event.preventDefault()
  next.focus()
  return true
}

/* ------------------------------------------------ grade (jspreadsheet, por coordenada) */

export interface SheetCoord {
  col: number
  row: number
}

/** Colunas de jornada da grade: as de horário (`time`) do modelo de folha. */
export function journeyColumnIndexes(columns: ReadonlyArray<{ type: string }>): number[] {
  return columns.flatMap((column, index) => (column.type === 'time' ? [index] : []))
}

/** Células de jornada vazias nas linhas exigidas, em ordem linha a linha (a do Tab). */
export function emptyJourneyCells(
  journeyColumns: number[],
  journeyRows: number[],
  readValue: (col: number, row: number) => unknown
): SheetCoord[] {
  const rows = [...new Set(journeyRows)].sort((a, b) => a - b)
  return rows.flatMap((row) =>
    journeyColumns
      .filter((col) => isJourneyValueEmpty(readValue(col, row)))
      .map((col) => ({ col, row }))
  )
}

/** Primeira célula vazia estritamente depois de `from` (linha a linha). */
export function nextEmptyJourneyCell(cells: SheetCoord[], from: SheetCoord): SheetCoord | null {
  return (
    cells.find((cell) => cell.row > from.row || (cell.row === from.row && cell.col > from.col)) ??
    null
  )
}
